import { useCallback, useEffect, useState } from "react";
import Editor from "react-simple-code-editor";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { useHighlightCode } from "./useHighlightCode";

export type SupportedSection = "head" | "body";

export const isSupportedSection = (
  section: string,
): section is SupportedSection => Object.keys(SECTION_LABELS).includes(section);

const SECTION_LABELS: Record<SupportedSection, string> = {
  head: t("builder_ui.custom_html_editor.sections.head"),
  body: t("builder_ui.custom_html_editor.sections.body"),
};

export interface CustomHtmlEditorProps {
  className?: string;
  initialSection?: SupportedSection;
  onChange: (value: string) => void;
  onSectionChange?: (section: string) => void;
  value: string;
}

const CustomHtmlEditorComponent = ({
  value,
  onChange,
  onSectionChange,
  initialSection = "body",
  className,
}: CustomHtmlEditorProps) => {
  const [section, setSection] = useState<SupportedSection>(initialSection);
  const { highlightCode } = useHighlightCode();

  const handleSectionChange = useCallback(
    (newSection: string) => {
      if (!isSupportedSection(newSection)) return;
      setSection(newSection);
      onSectionChange?.(newSection);
    },
    [onSectionChange],
  );

  useEffect(() => {
    setSection(initialSection);
  }, [initialSection]);

  return (
    <div
      className={classNames(
        "bg-primary border-primary flex h-full min-h-72 flex-col rounded-lg border",
        className,
      )}
    >
      <div className="border-primary flex w-full items-center justify-end border-b p-2">
        <Dropdown
          size="small"
          options={Object.entries(SECTION_LABELS).map(([value, label]) => ({
            label,
            value,
          }))}
          onChange={handleSectionChange}
          button={
            <button
              type="button"
              className="border-primary text-dark flex items-center justify-between gap-px rounded-lg border p-1 text-xs"
            >
              {SECTION_LABELS[section]}
              <Icon type="16-chevron-down" size={16} />
            </button>
          }
        />
      </div>
      <Editor
        value={value}
        onValueChange={onChange}
        highlight={highlightCode}
        padding={8}
        tabSize={2}
        textareaId="code-editor-textarea"
        textareaClassName="outline-none h-full"
        className="h-full grow font-mono text-sm"
      />
    </div>
  );
};

export default CustomHtmlEditorComponent;
